import AddIcon from '@mui/icons-material/Add';
import Fab from '@mui/material/Fab';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PerilConfigurationResponse } from '../../../api/ConfigApi';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { getNewPeril } from '../../../context/policy';
import {
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import { Exposure, PerilCreateRequest } from '../../../types/policy';
import { sortByObjectKey } from '../../../util/util';
import { StyledModalContentContainer } from '../../common/index';
import DefaultDialog from '../../DefaultDialog';
import { errorToast, successToast } from '../../Toast';
import ReactSelect, { SelectValue } from '../fields/ReactSelect';
import { getExposureIndexFromContext } from '../util_sharedEndorsementAndRenewal';

interface Props {
	authData: AuthData;
	perilConfigs: any[];
	exposureLocator: string;
	renewalData: RenewalData;
	redirectToExposureList?: boolean; // optional value to redirect to exposure list when new exposure locator is unknown, only applies to added exposures.
}

/**
 * The modal to add a peril to an exposure on an renewal. The exposure can either be existing or added.
 */
function AddPerilModal(props: Props) {
	const { t } = useTranslation();
	const { perilConfigs, exposureLocator, renewalData, authData } = props;
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;

	const sortedPerils = sortByObjectKey(perilConfigs, 'displayName');
	const [formLoading, setFormLoading] = React.useState(false);
	const [isOpen, setIsOpen] = React.useState(false);
	const [selectValue, setSelectValue] = React.useState({
		value: sortedPerils[0].name,
		label: sortedPerils[0].displayName,
	});
	const close = () => setIsOpen(false);
	const open = () => setIsOpen(true);
	const perilOptions = sortedPerils.map((pC: PerilConfigurationResponse) => ({
		value: pC.name,
		label: pC.displayName,
	}));
	const params = useParams();
	const { policyId, renewalId } = params;
	const navigate = useNavigate();

	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const { renewalUpdateObj, setLoading, selectedExposure } = renewalData;
	const { saveFastForm } = renewalData;

	const addPeril = async () => {
		const perilConfig = perilConfigs.find(
			(config) => config.name === selectValue.value,
		);
		const newPeril: PerilCreateRequest = getNewPeril(
			perilConfig,
			hidePerilLimitsEnabled,
		);

		const exposureIsAdded = !(selectedExposure as Exposure).displayId;
		// exposure index--either added or existing
		let exposureIndex = getExposureIndexFromContext(
			renewalUpdateObj,
			exposureLocator,
		);

		try {
			setLoading(true);
			setFormLoading(true);
			// call saveFastForm to add peril to exposure and save any pending form changes
			const updatedRenewal = await saveFastForm(
				getValues(),
				dirtyFields,
				reset,
				{
					addPeril: {
						exposureLocator,
						peril: newPeril,
						exposureIsAdded,
						exposureIndex,
					},
				},
			);

			let addedPerilIndex;
			if (exposureIsAdded) {
				addedPerilIndex =
					updatedRenewal.addExposures[exposureIndex].perils?.length - 1 + '';
				// navigate to new peril within new exposure
				navigate(
					`/policy/${policyId}/renewals/${renewalId}/exposures/added-${exposureIndex}/perils/${
						addedPerilIndex ? 'added-' + addedPerilIndex : ''
					}`,
				);
			} else {
				exposureIndex = getExposureIndexFromContext(
					{
						...updatedRenewal,
					},
					exposureLocator,
				);
				addedPerilIndex =
					updatedRenewal.updateExposures[exposureIndex].addPerils?.length -
					1 +
					'';
				// navigate to new peril within edited exposure
				navigate(
					`/policy/${policyId}/renewals/${renewalId}/exposures/${
						(selectedExposure as Exposure).displayId
					}/perils/${addedPerilIndex ? 'added-' + addedPerilIndex : ''}`,
				);
			}
			successToast('Successfully added peril');
		} catch (error) {
			errorToast('Error adding peril');
			console.error(error);
		} finally {
			setLoading(false);
			setFormLoading(false);
			close();
		}
	};

	return (
		<>
			<Fab
				color='primary'
				onClick={open}
				sx={{ position: 'fixed', bottom: 20, right: 20 }}
				variant='extended'
			>
				<AddIcon sx={{ mr: 1 }} /> {`Add ${t('peril.singular.upper')}`}
			</Fab>

			<DefaultDialog
				actions={[
					{ text: 'Add', onClick: addPeril, disabled: formLoading },
					{ text: 'Cancel', onClick: close },
				]}
				open={isOpen}
				onClose={close}
				title={`Add ${t('peril.singular.upper')}`}
			>
				<StyledModalContentContainer>
					<div style={{ width: '50%', height: '40px' }}>
						<ReactSelect
							id='add-peril-selector'
							options={perilOptions}
							value={selectValue}
							onChange={(opt: SelectValue) => setSelectValue(opt)}
							disableOpenMenuOnFocus
						/>
					</div>
				</StyledModalContentContainer>
			</DefaultDialog>
		</>
	);
}

export default withRenewalContext<Props>(AddPerilModal);
